import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { addChapters, readEPUB } from "../api";
import MutationError from "../components/MutationError";
import { MAX_CHAPTER_CHARS } from "../components/textfile";
import { useT } from "../i18n";
import { chaptersToAdd, initiallyKept } from "./epubchoice";

/**
 * EpubImport turns an EPUB into chapters of a book (ADR-0052).
 *
 * The server reads the file and sends back its chapters; the author unticks
 * what they do not want — front matter, a copyright page — and the rest are
 * added in one request, all of them or none. The bodies stay in memory between
 * the two calls, so nothing is kept on the server while the author chooses.
 */
export default function EpubImport({
  bookID,
  file,
  onAdded,
  onCancel,
}: {
  bookID: string;
  file: File;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [kept, setKept] = useState<boolean[]>([]);

  const read = useMutation({
    mutationFn: () => readEPUB(bookID, file),
    onSuccess: (got) => setKept(initiallyKept(got.chapters)),
  });
  const add = useMutation({
    mutationFn: () => {
      const got = read.data!;
      return addChapters(bookID, {
        chapters: chaptersToAdd(got.chapters, kept, t.file.untitled),
        language: got.language,
        author: got.author,
      });
    },
    onSuccess: onAdded,
  });

  // Read once per file. A mutation rather than a query: it is a POST of the
  // whole file, and nothing should ever refetch it behind the author's back.
  const { mutate } = read;
  useEffect(() => mutate(), [file, mutate]);

  const chapters = read.data?.chapters ?? [];
  const count = chapters.filter((c, i) => kept[i] && !c.too_long).length;

  return (
    <div data-testid="epub-import" className="space-y-3">
      {read.isPending && (
        <p data-testid="epub-reading" className="text-sm text-muted">
          {t.file.reading(file.name)}
        </p>
      )}
      <MutationError error={read.error} fallback={t.file.readFailed} testId="epub-error" />

      {read.data && (
        <>
          <p data-testid="epub-found" className="text-sm text-muted">
            {t.file.found(file.name, chapters.length)}
          </p>
          <ul className="divide-y divide-line rounded-control border border-line">
            {chapters.map((c, i) => (
              <li key={i}>
                <label
                  data-testid="epub-chapter"
                  className={`flex items-center gap-3 px-3 py-2.5 ${
                    c.too_long ? "opacity-60" : "cursor-pointer hover:bg-sunk"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!c.too_long && !!kept[i]}
                    disabled={c.too_long || add.isPending}
                    onChange={(e) =>
                      setKept((now) => now.map((k, j) => (j === i ? e.target.checked : k)))
                    }
                    className="size-5 shrink-0 accent-sage"
                  />
                  <span className="min-w-0 flex-1">
                    {/* dir="auto" on the title alone: it is authored text, and
                        nothing inside it carries a direction of its own. Inline,
                        as in ChapterRow, so the row lines up with the
                        interface while the title shapes in its own direction. */}
                    <span className="block truncate text-sm font-medium">
                      <span dir="auto">{c.title || t.file.untitled(i + 1)}</span>
                    </span>
                    <span className="block text-xs text-muted">
                      {c.too_long
                        ? t.file.tooLongToAdd(MAX_CHAPTER_CHARS)
                        : t.common.characters(c.chars)}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {read.data && (
          <button
            type="button"
            data-testid="epub-add"
            disabled={count === 0 || add.isPending}
            onClick={() => add.mutate()}
            className="rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {t.file.addChapters(count)}
          </button>
        )}
        <button
          type="button"
          data-testid="epub-cancel"
          disabled={add.isPending}
          onClick={onCancel}
          className="rounded-control border border-line px-3 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {t.file.another}
        </button>
      </div>
      <MutationError error={add.error} fallback={t.file.addFailed} testId="epub-add-error" />
    </div>
  );
}
